import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Button, Space } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, FullscreenOutlined } from '@ant-design/icons';

const FlowChartRenderer = ({ data, config = {} }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 600 });

  useEffect(() => {
    if (containerRef.current) {
      const width = config.width || containerRef.current.offsetWidth || 800;
      const height = config.height || 600;
      setDimensions({ width, height });
    }
  }, [config]);

  useEffect(() => {
    if (!data || !dimensions.width) return;
    try {
      renderFlowchart();
    } catch (error) {
      console.error('Flowchart rendering error:', error);
    }
  }, [data, dimensions]);

  const renderFlowchart = () => {
    const svgElement = svgRef.current;
    if (!svgElement) return;

    // Clear previous
    d3.select(svgElement).selectAll('*').remove();

    const width = dimensions.width;
    const height = dimensions.height;
    const margin = { top: 60, right: 60, bottom: 60, left: 60 };

    const svg = d3.select(svgElement)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    // Add gradient definitions
    const defs = svg.append('defs');

    // Gradient for start nodes
    const startGradient = defs.append('linearGradient')
      .attr('id', 'startGradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '100%');
    startGradient.append('stop').attr('offset', '0%').attr('stop-color', '#48bb78');
    startGradient.append('stop').attr('offset', '100%').attr('stop-color', '#38a169');

    // Gradient for process nodes
    const processGradient = defs.append('linearGradient')
      .attr('id', 'processGradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '100%');
    processGradient.append('stop').attr('offset', '0%').attr('stop-color', '#4299e1');
    processGradient.append('stop').attr('offset', '100%').attr('stop-color', '#3182ce');

    // Gradient for decision nodes
    const decisionGradient = defs.append('linearGradient')
      .attr('id', 'decisionGradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '100%');
    decisionGradient.append('stop').attr('offset', '0%').attr('stop-color', '#ed8936');
    decisionGradient.append('stop').attr('offset', '100%').attr('stop-color', '#dd6b20');

    // Gradient for end nodes
    const endGradient = defs.append('linearGradient')
      .attr('id', 'endGradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '100%');
    endGradient.append('stop').attr('offset', '0%').attr('stop-color', '#f56565');
    endGradient.append('stop').attr('offset', '100%').attr('stop-color', '#e53e3e');

    // Shadow filter
    const filter = defs.append('filter')
      .attr('id', 'flowShadow')
      .attr('x', '-50%').attr('y', '-50%')
      .attr('width', '200%').attr('height', '200%');
    filter.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 3);
    filter.append('feOffset').attr('dx', 0).attr('dy', 2).attr('result', 'offsetblur');
    filter.append('feComponentTransfer').append('feFuncA').attr('type', 'linear').attr('slope', 0.3);
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    const g = svg.append('g');

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.3, 2])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Define node types and their styles
    const nodeStyles = {
      start: { fill: 'url(#startGradient)', shape: 'ellipse' },
      end: { fill: 'url(#endGradient)', shape: 'ellipse' },
      process: { fill: 'url(#processGradient)', shape: 'rect' },
      decision: { fill: 'url(#decisionGradient)', shape: 'diamond' },
      default: { fill: 'url(#processGradient)', shape: 'rect' },
    };

    // Simple layout algorithm
    const nodes = data.nodes || [];
    const edges = data.edges || [];

    // Calculate text dimensions for each node
    const tempText = svg.append('text')
      .attr('font-size', '11px')
      .attr('font-weight', 'bold')
      .style('visibility', 'hidden');

    nodes.forEach(node => {
      // Split text into lines
      const words = node.label.split(/\s+/);
      const maxCharsPerLine = 15;
      const lines = [];
      let currentLine = [];
      
      words.forEach(word => {
        if ((currentLine.join(' ') + ' ' + word).length > maxCharsPerLine && currentLine.length > 0) {
          lines.push(currentLine.join(' '));
          currentLine = [word];
        } else {
          currentLine.push(word);
        }
      });
      if (currentLine.length > 0) {
        lines.push(currentLine.join(' '));
      }
      
      // Calculate dimensions
      const maxLineWidth = Math.max(...lines.map(line => {
        tempText.text(line);
        return tempText.node().getComputedTextLength();
      }));
      
      node.textLines = lines;
      // Diamonds need extra space due to their shape
      const extraPadding = node.type === 'decision' ? 60 : 30;
      node.width = Math.max(120, maxLineWidth + extraPadding);
      node.height = Math.max(60, lines.length * 14 + 30);
      
      // Make diamonds significantly wider to accommodate diagonal edges
      if (node.type === 'decision') {
        node.width = Math.max(node.width, node.height * 1.6);
        node.height = Math.max(node.height, 80); // Minimum height for diamonds
      }
    });

    tempText.remove();

    // Position nodes in a grid
    const cols = Math.ceil(Math.sqrt(nodes.length));
    const spacing = { x: 220, y: 180 };

    nodes.forEach((node, i) => {
      node.x = (i % cols) * spacing.x + node.width / 2;
      node.y = Math.floor(i / cols) * spacing.y + node.height / 2;
    });

    // Add arrowhead marker
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('markerWidth', 12)
      .attr('markerHeight', 12)
      .attr('refX', 11)
      .attr('refY', 3)
      .attr('orient', 'auto')
      .append('polygon')
      .attr('points', '0 0, 12 3, 0 6')
      .attr('fill', '#667eea');

    // Draw edges with animation
    const link = g.selectAll('.link')
      .data(edges)
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('d', d => {
        const source = nodes.find(n => n.id === d.from);
        const target = nodes.find(n => n.id === d.to);
        if (!source || !target) return '';
        
        const midX = (source.x + target.x) / 2;
        return `M ${source.x} ${source.y} 
                C ${midX} ${source.y}, ${midX} ${target.y}, ${target.x} ${target.y}`;
      })
      .attr('fill', 'none')
      .attr('stroke', '#667eea')
      .attr('stroke-width', 3)
      .attr('stroke-opacity', 0.8)
      .attr('marker-end', 'url(#arrowhead)')
      .attr('opacity', 0);

    // Animate edges
    link.transition()
      .duration(800)
      .delay((d, i) => i * 100)
      .attr('opacity', 1);

    // Draw nodes with animation
    const node = g.selectAll('.node')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.x},${d.y})`)
      .style('opacity', 0)
      .style('cursor', 'pointer');

    // Node shapes (adaptive sizing with enhanced styling)
    node.each(function(d) {
      const style = nodeStyles[d.type] || nodeStyles.default;
      const g = d3.select(this);
      const w = d.width;
      const h = d.height;

      if (style.shape === 'ellipse') {
        g.append('ellipse')
          .attr('rx', w / 2)
          .attr('ry', h / 2)
          .attr('fill', style.fill)
          .attr('stroke', 'rgba(255,255,255,0.8)')
          .attr('stroke-width', 3)
          .attr('filter', 'url(#flowShadow)');
      } else if (style.shape === 'diamond') {
        g.append('path')
          .attr('d', `M 0,-${h/2} L ${w/2},0 L 0,${h/2} L -${w/2},0 Z`)
          .attr('fill', style.fill)
          .attr('stroke', 'rgba(255,255,255,0.8)')
          .attr('stroke-width', 3)
          .attr('filter', 'url(#flowShadow)');
      } else {
        g.append('rect')
          .attr('x', -w / 2)
          .attr('y', -h / 2)
          .attr('width', w)
          .attr('height', h)
          .attr('rx', 12)
          .attr('fill', style.fill)
          .attr('stroke', 'rgba(255,255,255,0.8)')
          .attr('stroke-width', 3)
          .attr('filter', 'url(#flowShadow)');
      }
    });

    // Animate nodes
    node.transition()
      .duration(800)
      .delay((d, i) => i * 100)
      .style('opacity', 1);

    // Add hover effects
    node.on('mouseenter', function() {
      d3.select(this)
        .transition()
        .duration(200)
        .attr('transform', d => `translate(${d.x},${d.y}) scale(1.05)`);
    });

    node.on('mouseleave', function() {
      d3.select(this)
        .transition()
        .duration(200)
        .attr('transform', d => `translate(${d.x},${d.y}) scale(1)`);
    });

    // Node labels (using pre-calculated lines, perfectly centered)
    node.each(function(d) {
      const textElement = d3.select(this).append('text')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', 'white')
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .attr('font-family', 'Arial, sans-serif')
        .style('text-shadow', '1px 1px 2px rgba(0,0,0,0.3)')
        .style('pointer-events', 'none');

      const lineHeight = 15;
      const totalHeight = d.textLines.length * lineHeight;
      const startY = -(totalHeight / 2) + (lineHeight / 2);

      d.textLines.forEach((line, i) => {
        textElement.append('tspan')
          .attr('x', 0)
          .attr('dy', i === 0 ? startY : lineHeight)
          .text(line);
      });
    });

    // Auto-fit the chart after rendering
    setTimeout(() => {
      const bounds = g.node().getBBox();
      const fullWidth = dimensions.width;
      const fullHeight = dimensions.height;
      const width = bounds.width;
      const height = bounds.height;
      const midX = bounds.x + width / 2;
      const midY = bounds.y + height / 2;
      
      const scale = 0.8 / Math.max(width / fullWidth, height / fullHeight);
      const translate = [fullWidth / 2 - scale * midX, fullHeight / 2 - scale * midY];
      
      svg.transition()
        .duration(750)
        .call(
          d3.zoom().transform,
          d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
        );
    }, 100);
  };

  const handleZoomIn = () => {
    d3.select(svgRef.current)
      .transition()
      .call(d3.zoom().scaleBy, 1.3);
  };

  const handleZoomOut = () => {
    d3.select(svgRef.current)
      .transition()
      .call(d3.zoom().scaleBy, 0.7);
  };

  const handleReset = () => {
    const svg = d3.select(svgRef.current);
    svg.transition()
      .duration(750)
      .call(
        d3.zoom().transform,
        d3.zoomIdentity
      );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
        <Space style={{ marginBottom: '12px' }}>
          <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} size="small">
            Zoom In
          </Button>
          <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} size="small">
            Zoom Out
          </Button>
          <Button icon={<FullscreenOutlined />} onClick={handleReset} size="small">
            Reset
          </Button>
        </Space>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <svg ref={svgRef} style={{ border: '1px solid #f0f0f0', borderRadius: '4px', maxWidth: '100%', height: 'auto' }} />
        </div>
      </div>
    </div>
  );
};

export default FlowChartRenderer;
